'use client';

import { type ReactNode, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import {
  Image as ImageIcon,
  Check,
  FileText,
  ChevronDown,
  ImageOff,
  ArrowRight,
} from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { LoadingSpinner } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import {
  useClassifiedProjectReferences,
  useUpdateProjectReferenceCategory,
} from '@/features/project-reference/hooks/use-project-references';
import type {
  ProjectReference,
  ProjectReferenceCategory,
} from '@/types/project-reference';
import {
  isImageReference,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
} from '@/types/project-reference';

const IMAGE_CATEGORIES: ProjectReferenceCategory[] = [
  'style',
  'color',
  'material',
  'output',
];

interface ProjectReferenceSheetProps {
  projectId: string;
  category: ProjectReferenceCategory;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  maxSelection?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback to navigate to the project references management page */
  onNavigateToReferences?: () => void;
}

function getReferenceImageUrl(reference: ProjectReference): string | null {
  if (reference.attributes?.file_url) {
    return reference.attributes.file_url;
  }
  return null;
}

/**
 * Controlled selection sheet for project references. Holds the grid of
 * matching/other references and the category-change affordance; the trigger
 * surface (a settings row, a reference-source radio, a compact button) lives
 * in the caller and drives `open`. Shared by `ProjectReferenceSelector`
 * (output style) and `ReferenceSourceGroup` (style/materials/colors).
 */
export function ProjectReferenceSheet({
  projectId,
  category,
  selectedIds,
  onSelectionChange,
  maxSelection = 3,
  open,
  onOpenChange,
  onNavigateToReferences,
}: ProjectReferenceSheetProps) {
  const t = useTranslations('projectReference');
  const tRender = useTranslations('render.settings.projectReference');

  // Fetch ALL classified project references (no category filter)
  const { data: allReferences = [], isLoading } =
    useClassifiedProjectReferences(projectId, undefined, {
      enabled: !!projectId,
    });

  const updateCategory = useUpdateProjectReferenceCategory(projectId);

  // Split into matching and other references
  const matchingReferences = allReferences.filter(
    (r) => r.attributes.category === category
  );
  const otherReferences = allReferences.filter(
    (r) => r.attributes.category !== category && isImageReference(r)
  );

  const handleToggle = (referenceId: string) => {
    if (selectedIds.includes(referenceId)) {
      onSelectionChange(selectedIds.filter((id) => id !== referenceId));
      return;
    }

    // Single-select pickers (e.g. output style) replace the current selection
    // on tap; without this branch tapping a second reference is a no-op while
    // one is selected.
    if (maxSelection === 1) {
      onSelectionChange([referenceId]);
      return;
    }

    if (selectedIds.length < maxSelection) {
      onSelectionChange([...selectedIds, referenceId]);
    }
  };

  const handleCategoryChange = (
    referenceId: string,
    newCategory: ProjectReferenceCategory
  ) => {
    updateCategory.mutate({ referenceId, category: newCategory });
  };

  const handleManageClick = () => {
    onOpenChange(false);
    onNavigateToReferences?.();
  };

  return (
    <NestedSheet open={open} onOpenChange={onOpenChange}>
      <NestedSheetContent>
        {/* Apple parity: short category-only title ("Style reference",
            "Colors", "Material sample", "Output style"). The "Select up to
            N references" subtitle inside the body conveys the action. */}
        <NestedSheetHeader>
          <NestedSheetTitle>{t(`category.${category}`)}</NestedSheetTitle>
        </NestedSheetHeader>

        <NestedSheetBody className="px-4 space-y-4">
          <p className="text-form-help text-muted-foreground">
            {tRender('selectUpTo', { max: maxSelection })}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : matchingReferences.length === 0 &&
            otherReferences.length === 0 ? (
            <div className="text-center py-6">
              <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-50" />
              <p className="text-form-help text-muted-foreground">
                {tRender('noRefsUploadHint')}
              </p>
              {onNavigateToReferences && (
                <button
                  type="button"
                  onClick={handleManageClick}
                  className="mt-3 inline-flex items-center gap-1 text-list-item-subtitle text-primary hover:underline"
                >
                  {tRender('manageAll')}
                  <ArrowRight className="w-4 h-4 rtl:-scale-x-100" />
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Matching references grid */}
              {matchingReferences.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {matchingReferences.map((reference) => {
                    const isRefSelected = selectedIds.includes(reference.id);
                    const isDisabled =
                      maxSelection > 1 &&
                      !isRefSelected &&
                      selectedIds.length >= maxSelection;
                    const isImage = isImageReference(reference);
                    const imageUrl = getReferenceImageUrl(reference);

                    return (
                      <ReferenceGridItem
                        key={reference.id}
                        reference={reference}
                        isImage={isImage}
                        imageUrl={imageUrl}
                        isSelected={isRefSelected}
                        isDisabled={isDisabled}
                        onToggle={() => handleToggle(reference.id)}
                      />
                    );
                  })}
                </div>
              )}

              {/* Other references section */}
              {otherReferences.length > 0 && (
                <div className="space-y-3">
                  <p className="text-section-eyebrow text-muted-foreground">
                    {tRender('otherReferences')}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {otherReferences.map((reference) => {
                      const isRefSelected = selectedIds.includes(reference.id);
                      const isDisabled =
                        maxSelection > 1 &&
                        !isRefSelected &&
                        selectedIds.length >= maxSelection;
                      const isImage = isImageReference(reference);
                      const imageUrl = getReferenceImageUrl(reference);

                      return (
                        <ReferenceGridItem
                          key={reference.id}
                          reference={reference}
                          isImage={isImage}
                          imageUrl={imageUrl}
                          isSelected={isRefSelected}
                          isDisabled={isDisabled}
                          onToggle={() => handleToggle(reference.id)}
                          categoryBadge={
                            reference.attributes.category ? (
                              <CategoryBadge
                                category={reference.attributes.category}
                                onCategoryChange={(newCat) =>
                                  handleCategoryChange(reference.id, newCat)
                                }
                              />
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Manage all references link */}
          {onNavigateToReferences &&
            (matchingReferences.length > 0 || otherReferences.length > 0) && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={handleManageClick}
                  className="inline-flex items-center gap-1 text-list-item-subtitle text-primary hover:underline"
                >
                  {tRender('manageAll')}
                  <ArrowRight className="w-4 h-4 rtl:-scale-x-100" />
                </button>
              </div>
            )}
        </NestedSheetBody>
      </NestedSheetContent>
    </NestedSheet>
  );
}

// Sub-component for selectable references
interface ReferenceGridItemProps {
  reference: ProjectReference;
  isImage: boolean;
  imageUrl: string | null;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
  categoryBadge?: ReactNode;
}

function ReferenceGridItem({
  reference,
  isImage,
  imageUrl,
  isSelected,
  isDisabled,
  onToggle,
  categoryBadge,
}: ReferenceGridItemProps) {
  const [imageError, setImageError] = useState(false);

  const showImage = isImage && imageUrl && !imageError;
  const showPlaceholder = !isImage || !imageUrl || imageError;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onToggle}
        disabled={isDisabled}
        className={cn(
          'relative aspect-[4/3] w-full rounded-lg overflow-hidden border-2 transition-all',
          isSelected
            ? 'border-primary ring-2 ring-primary/20'
            : isDisabled
              ? 'border-border opacity-50 cursor-not-allowed'
              : 'border-border hover:border-muted-foreground'
        )}
      >
        {showImage && (
          <Image
            src={imageUrl}
            alt={reference.attributes.metadata?.original_filename || 'Reference'}
            fill
            className="object-cover"
            onError={() => setImageError(true)}
          />
        )}

        {showPlaceholder && (
          <div className="absolute inset-0 bg-muted flex items-center justify-center">
            {isImage ? (
              <ImageOff className="w-8 h-8 text-muted-foreground" />
            ) : (
              <FileText className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        )}

        {/* Filename overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
          <p className="text-card-meta text-white truncate">
            {reference.attributes.original_filename ||
              reference.attributes.metadata?.original_filename}
          </p>
        </div>
      </button>
      {categoryBadge}
    </div>
  );
}

// Sub-component for category badge with dropdown
interface CategoryBadgeProps {
  category: ProjectReferenceCategory;
  onCategoryChange: (category: ProjectReferenceCategory) => void;
}

function CategoryBadge({ category, onCategoryChange }: CategoryBadgeProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-full text-chip',
          CATEGORY_COLORS[category]
        )}
      >
        {CATEGORY_LABELS[category]}
        <ChevronDown className="w-3 h-3" />
      </button>

      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 bg-popover border border-border rounded-md shadow-md py-1 min-w-[140px]">
            {IMAGE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onCategoryChange(cat);
                }}
                className="w-full text-left px-3 py-1.5 text-list-item-subtitle text-popover-foreground hover:bg-accent transition-colors"
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
